import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Grid, Stack, Typography } from "@mui/material";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ContractorPortalApi from "../../../../../infrastructure/api/contractorPortalApi";
import { formatNumber } from "../../../../../infrastructure/utils/formatters";
import ContractorPortalPageShell from "../components/ContractorPortalPageShell";
import ContractorStatCard from "../components/ContractorStatCard";
import { ContractNextActionCard } from "../components/contract-ux";
import { CONTRACTOR_PORTAL_BASE } from "../contractorPortalNav";
import {
  CONTRACTOR_CONTRACT_PHASE,
  resolveContractorContractUx,
} from "../utils/contractorContractUx";

/**
 * Dashboard = overview only (stats + one next-step CTA).
 * Full contract / guarantee workflow lives under تب قراردادها.
 */
const ContractorDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await ContractorPortalApi.getDashboardSummary();
      if (cancelled) return;
      if (!res?.isSuccess) {
        setError(res?.message || "خطا در بارگذاری داشبورد.");
        setSummary(null);
      } else {
        setSummary(res.data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const master = summary?.master;
  const ux = useMemo(
    () =>
      resolveContractorContractUx(master, {
        contractsPath: `${CONTRACTOR_PORTAL_BASE}/contracts`,
        projectsPath: `${CONTRACTOR_PORTAL_BASE}/projects`,
      }),
    [master]
  );

  const statusSummary = summary?.executionStatusSummary || [];
  const hasStats =
    summary &&
    (summary.activeContracts ||
      summary.activeProjects ||
      summary.pendingPayments ||
      summary.openDefects ||
      statusSummary.length);

  const dashboardAction = useMemo(() => {
    if (!master || !ux?.action) return null;

    const contractsHref = master.contractId
      ? `${CONTRACTOR_PORTAL_BASE}/contracts/${master.contractId}`
      : `${CONTRACTOR_PORTAL_BASE}/contracts`;

    if (ux.phase === CONTRACTOR_CONTRACT_PHASE.ACTIVE) {
      return ux.action;
    }

    if (
      ux.phase === CONTRACTOR_CONTRACT_PHASE.PENDING_SIGNATURE ||
      ux.phase === CONTRACTOR_CONTRACT_PHASE.AWAITING_GUARANTEE
    ) {
      return {
        ...ux.action,
        to: contractsHref,
        scrollTo: undefined,
        tip: "جزئیات و بارگذاری مدارک در بخش قراردادها انجام می‌شود.",
      };
    }

    if (
      ux.phase === CONTRACTOR_CONTRACT_PHASE.GUARANTEE_REVIEW ||
      ux.phase === CONTRACTOR_CONTRACT_PHASE.WAITING_COMPLETION
    ) {
      return {
        ...ux.action,
        to: contractsHref,
        scrollTo: undefined,
        buttonLabel: "مشاهده قرارداد",
        tip: "جزئیات کامل در تب قراردادها.",
      };
    }

    return null;
  }, [master, ux]);

  return (
    <Stack spacing={1.5} dir="rtl" data-testid="contractor-dashboard-page">
      <BreadcrumbsComponent />
      <ContractorPortalPageShell
        title="داشبورد پیمانکار"
        description="خلاصه وضعیت همکاری، پروژه‌ها و اقدام بعدی"
        loading={loading}
        error={error}
      >
        {dashboardAction ? (
          <Box sx={{ mb: 2 }}>
            <ContractNextActionCard action={dashboardAction} />
          </Box>
        ) : null}

        {master ? (
          <Alert severity="info" sx={{ mb: 2, py: 1 }} data-testid="dashboard-contract-hint">
            وضعیت قرارداد: <strong>{ux.friendlyStatus}</strong>
            {" — "}
            جزئیات، دانلود و ضمانت را از تب «قراردادها» پیگیری کنید.
          </Alert>
        ) : null}

        {hasStats ? (
          <Stack spacing={2} data-testid="contractor-dashboard-content">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <ContractorStatCard
                  testId="stat-active-contracts"
                  label="قراردادهای فعال"
                  value={formatNumber(summary.activeContracts || 0)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ContractorStatCard
                  testId="stat-active-projects"
                  label="پروژه‌های فعال"
                  value={formatNumber(summary.activeProjects || 0)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ContractorStatCard
                  testId="stat-pending-payments"
                  label="پرداخت‌های در انتظار"
                  value={formatNumber(summary.pendingPayments || 0)}
                  highlight={Boolean(summary.pendingPayments)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ContractorStatCard
                  testId="stat-open-defects"
                  label="ایرادات باز"
                  value={formatNumber(summary.openDefects || 0)}
                  highlight={Boolean(summary.openDefects)}
                />
              </Grid>
            </Grid>

            {statusSummary.length ? (
              <Stack spacing={1} data-testid="execution-status-summary">
                <Typography variant="subtitle1" fontWeight={700}>
                  خلاصه وضعیت اجرا
                </Typography>
                <Grid container spacing={2}>
                  {statusSummary.map((row) => (
                    <Grid item xs={12} sm={6} md={4} key={row.statusTitle}>
                      <ContractorStatCard
                        label={row.statusTitle}
                        value={formatNumber(row.count)}
                        testId={`stat-exec-${row.statusTitle}`}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            ) : null}
          </Stack>
        ) : null}

        {!master && !hasStats && !loading ? (
          <Alert
            severity="info"
            data-testid="contractor-dashboard-empty"
            sx={{ py: 1 }}
          >
            هنوز داده فعالی برای نمایش وجود ندارد. پس از تخصیص پروژه یا فعال‌سازی
            قرارداد، خلاصه اینجا نمایش داده می‌شود.
          </Alert>
        ) : null}
      </ContractorPortalPageShell>
    </Stack>
  );
};

export default ContractorDashboardPage;
